import { useEffect, useState } from 'react';
import * as storage from './storage.js';

export const ONBOARDING_COMPLETE_STORAGE_KEY = 'onboardingComplete';

const listeners = new Set<() => void>();

export function isOnboardingComplete(): boolean {
  return storage.get<boolean>(ONBOARDING_COMPLETE_STORAGE_KEY) === true;
}

export function hasSavedAppSettings(): boolean {
  return storage.get<unknown>('appSettings') !== undefined;
}

export function needsOnboarding(): boolean {
  return !isOnboardingComplete() && !hasSavedAppSettings();
}

export function completeOnboarding(): void {
  storage.set(ONBOARDING_COMPLETE_STORAGE_KEY, true);
  listeners.forEach((fn) => fn());
}

export function getSafeOnboardingReturnPath(value: unknown): string {
  if (typeof value === 'string' && isSafeAppPath(value)) return value;
  return '/';
}

function isSafeAppPath(value: string): boolean {
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('://')) return false;
  // Reject backslashes and control characters that could smuggle an off-app destination.
  if (value.includes('\\')) return false;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return false;
  }
  // Never bounce back into onboarding itself — that would trap the user in a loop.
  return !value.startsWith('/onboarding');
}

export function resetOnboarding(): void {
  storage.remove(ONBOARDING_COMPLETE_STORAGE_KEY);
  listeners.forEach((fn) => fn());
}

export function useNeedsOnboarding(): boolean {
  const [needsSetup, setNeedsSetup] = useState(() => needsOnboarding());

  useEffect(() => {
    const listener = () => setNeedsSetup(needsOnboarding());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return needsSetup;
}
